import React, { useState, useEffect } from 'react';
import { FaFolder, FaChevronRight, FaArrowLeft, FaTimes } from 'react-icons/fa';
import folderService from '../../services/folderService';
import { notify } from '../CustomToast';
import { useI18n } from '../../i18n';
import './MoveModal.css';

const MoveModal = ({ isOpen, onClose, onMove, item, currentFolderId }) => {
    const { t } = useI18n();
    const [currentPath, setCurrentPath] = useState(null); // null means root
    const [subfolders, setSubfolders] = useState([]);
    const [breadcrumb, setBreadcrumb] = useState([{ id: null, name: t('pages.file_manager.my_folders') }]);
    const [loading, setLoading] = useState(false);
    const [isMoving, setIsMoving] = useState(false);

    // Initial load
    useEffect(() => {
        if (isOpen) {
            loadFolders(null);
            setBreadcrumb([{ id: null, name: t('pages.file_manager.my_folders') }]);
            setCurrentPath(null);
        }
    }, [isOpen, t]);

    const loadFolders = async (folderId) => {
        setLoading(true);
        try {
            let folders = [];
            if (folderId === null) {
                const response = await folderService.getRootFolders();
                if (response?.data) {
                    // Combine owned and shared
                    folders = [
                        ...(response.data.owned || []),
                        ...(response.data.shared || [])
                    ];
                }
            } else {
                const response = await folderService.getFolderContents(folderId);
                if (response?.data) {
                    folders = response.data.subfolders || [];
                }
            }
            setSubfolders(folders);
        } catch (err) {
            console.error(err);
            notify('error', t('pages.file_manager.move_modal.errors.load_error'));
        } finally {
            setLoading(false);
        }
    };

    const handleNavigate = (folder) => {
        setCurrentPath(folder.id);
        setBreadcrumb(prev => [...prev, { id: folder.id, name: folder.name }]);
        loadFolders(folder.id);
    };

    const handleNavigateUp = () => {
        if (breadcrumb.length <= 1) return;
        const newBreadcrumb = breadcrumb.slice(0, -1);
        const parent = newBreadcrumb[newBreadcrumb.length - 1];
        setBreadcrumb(newBreadcrumb);
        setCurrentPath(parent.id);
        loadFolders(parent.id);
    };

    const handleMove = async () => {
        if (isMoving) return;

        // Validation: Cannot move folder into itself
        if (item.type === 'folder' && item.id === currentPath) {
            notify('error', t('pages.file_manager.move_modal.errors.move_self'));
            return;
        }

        setIsMoving(true);
        try {
            await onMove(item.id, currentPath);
            // Parent handles close
        } catch (error) {
            console.error(error);
        } finally {
            setIsMoving(false);
        }
    };

    if (!isOpen) return null;

    // Filter out item itself if it's a folder
    const displayFolders = subfolders.filter(f => item.type !== 'folder' || f.id !== item.id);

    return (
        <div className="project-composer-overlay" onClick={(e) => { if (e.target === e.currentTarget && !isMoving) onClose(); }}>
            <div className="project-composer-modal" style={{ width: '600px', maxWidth: 'calc(100% - 32px)', margin: '0 16px', display: 'flex', flexDirection: 'column', maxHeight: '90vh' }}>
                <div className="project-composer-header">
                    <h2 className="project-composer-title">
                        {t('pages.file_manager.move_modal.title', { name: item.name || item.original_filename })}
                    </h2>
                    <button className="project-composer-close" onClick={onClose} disabled={isMoving}>
                        <FaTimes />
                    </button>
                </div>

                <div className="project-composer-body" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <div className="move-breadcrumb">
                        {breadcrumb.length > 1 && (
                            <button className="back-btn" onClick={handleNavigateUp}>
                                <FaArrowLeft />
                            </button>
                        )}
                        <span className="current-path">
                            {breadcrumb.map(b => b.name).join(' / ')}
                        </span>
                    </div>

                    <div className="folder-list">
                        {loading ? (
                            <div className="loading-spinner">{t('common.status.loading')}</div>
                        ) : displayFolders.length === 0 ? (
                            <div className="empty-folder">{t('pages.file_manager.move_modal.no_folders')}</div>
                        ) : (
                            displayFolders.map(folder => (
                                <div
                                    key={folder.id}
                                    className="folder-item"
                                    onClick={() => handleNavigate(folder)}
                                >
                                    <FaFolder className="folder-icon" />
                                    <span className="folder-name">{folder.name}</span>
                                    <FaChevronRight className="chevron-icon" />
                                </div>
                            ))
                        )}
                    </div>
                </div>

                <div className="project-composer-footer">
                    <button
                        className="project-composer-btn project-composer-btn-secondary"
                        onClick={onClose}
                        disabled={isMoving}
                    >
                        {t('common.actions.cancel')}
                    </button>
                    <button
                        className="project-composer-btn project-composer-btn-primary"
                        onClick={handleMove}
                        disabled={
                            isMoving ||
                            loading ||
                            (item.type === 'file' && currentPath === null) ||
                            (item.type === 'folder' && currentPath === item.parent_id) ||
                            (item.type === 'file' && currentPath === item.folder_id)
                        }
                    >
                        {isMoving ? t('common.status.moving') : t('pages.file_manager.move_modal.move_here')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MoveModal;
